import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';

import { isRequired } from 'components/Questionnaires/validators';
import { sortArrayByLabel } from 'common/language/intlArrayHelper';

export const QuestionDropDownField = ({ intl, blockId, question, className, onChange }) => {
  const [inputValue, setInputValue] = useState(question?.answer ?? question?.default ?? '');

  const onInputChange = (event) => {
    setInputValue(event.target.value);
    onChange(question, event.target.value);
    return true;
  };

  const questionProps = () => {
    const props = Object.assign({}, question.componentProps);
    props.items = undefined; // Clear items so it doesn't display in html
    // eslint-disable-next-line react/prop-types
    delete props.preventTranslation; // Clear preventTranslation so it doesn't display in html
    return props;
  };

  function getLabel(option) {
    if (!option.label) {
      return option.value;
    }
    return typeof question.componentProps.items === 'string' || question?.componentProps?.preventTranslation === true ? option.label : intl.formatMessage({ id: option.label });
  }

  useEffect(() => {
    if (inputValue || inputValue === question.default) {
      onChange(question, inputValue);
    }
  }, []);

  useEffect(() => {
    if (inputValue !== question.answer) {
      setInputValue(question.answer ?? question.default ?? '');
    }
  }, [question.answer]);

  const items = useMemo(() => {
    if (!question?.componentProps?.items) {
      return [];
    }

    if (typeof question.componentProps.items === 'string') {
      const intlItems = intl.messages[question.componentProps.items];
      if (intlItems && Array.isArray(intlItems)) {
        return sortArrayByLabel(intlItems, question.placeholderValue ?? '', question.otherValue ?? 'Other');
      }
      return [];
    }
    if (Array.isArray(question.componentProps.items)) {
      return sortArrayByLabel(question.componentProps.items, question.placeholderValue ?? '', question.otherValue ?? 'Other');
    }
    return [];
  }, [question?.componentProps?.items, intl]);

  return (<select
    key={ `${ blockId }-${ question.id }` }
    id={ `${ blockId }-${ question.id }` }
    name={ question.id }
    value={ inputValue }
    onChange={ e => onInputChange(e) }
    autoComplete="no"
    className={ `${ className }${ question.isValid === false ? ' error' : '' }` }
    required={ isRequired(question) }
    { ...questionProps() }
    disabled={ question.disabled }
    aria-label={ (question.placeholder ?? question.label) ? intl.formatMessage({ id: question.placeholder ?? question.label }) : '' }
    aria-required={ isRequired(question) }
    aria-describedby={ question.isValid === false ? (`${ blockId }-${ question.id }-error`) : undefined }
  >
    { items?.map(o => <option key={ o.value } value={ o.value }>{ getLabel(o) }</option>) }
  </select>);
};

QuestionDropDownField.displayName = 'QuestionDropDownField';

QuestionDropDownField.propTypes = {
  intl: PropTypes.shape().isRequired,
  blockId: PropTypes.string.isRequired,
  question: PropTypes.shape().isRequired,
  className: PropTypes.string,
  onChange: PropTypes.func.isRequired
};

QuestionDropDownField.defaultProps = {
  className: ''
};

export default QuestionDropDownField;
